// NowOfferingRotation
// ===================
// Editorial "Now Offering" treatment-discovery rail for the homepage.
//
// Why it exists:
//   • Clients told the owner they couldn't tell what's new at the clinic vs.
//     classics. This rail rotates a curated lineup ‑ new services
//     (RF Microneedling, HydraFacial, Laser Hair Removal) up front, then
//     core injectables ‑ each with a short blurb and a direct deep-link.
//   • Replaces the "everything looks the same" feeling: every card has its
//     own champagne badge, accent border, and CTA.
//
// Behaviour:
//   • On mobile/tablet (<lg) renders a horizontal-scroll snap carousel so
//     users can swipe through every offering.
//   • On desktop renders a 4-column grid for at-a-glance visibility.
//   • Auto-advances a tiny "Spotlight" pill across the cards every 4.5 s
//     to draw the eye, but never auto-scrolls — clients keep control.
//   • Each card is a `<Link>` ‑ no JS event handlers needed for routing,
//     so the SEO crawler indexes every treatment page from the home page.

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Sparkles, Zap, Droplets, Syringe, Heart, Crown, Flame, ArrowRight, Leaf,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACCENT = "#c9a86b";        // champagne
const ACCENT_DEEP = "#a98455";   // bronze

// ───────────────────────────────────────────────────────────────────────────
// Curated lineup. Order matters — "Now Offering" novelty items first, then
// classics. Each entry maps to a real page on the site so deep-links work.
const OFFERINGS = [
  {
    id: "eminence-facials-body",
    badge: "New · Facials & Body",
    name: "Éminence Facials & Body",
    nameFr: "Soins Éminence · Visage & Corps",
    tagline: "Signature Glow · Stone Crop Ritual · Ottawa only",
    taglineFr: "Signature Glow · Stone Crop · Ottawa seulement",
    blurb:
      "Certified-organic facials and body rituals hand-crafted from fresh fruit, herbs and botanicals — from the Signature Glow Facial to our 90-minute Stone Crop Head-to-Toe Ritual. With a Medical Aesthetician at our Ottawa clinic.",
    blurbFr:
      "Soins visage et rituels corps certifiés biologiques à base de fruits, herbes et botaniques frais — du Signature Glow au Rituel Stone Crop 90 min. Avec une esthéticienne médicale à notre clinique d'Ottawa.",
    href: "/treatments/eminence-facials",
    Icon: Leaf,
    accent: "#6ee7b7",
    // Kling 2 (fal.ai) reels — cycles every 8s across all 4 rituals so
    // the homepage card teases the full Éminence body-treatment menu.
    videos: [
      "/stone-crop-15s.mp4",
      "/firm-skin.mp4",
      "/coconut-sugar.mp4",
      "/eight-greens.mp4",
    ],
    videoLabels: [
      "Stone Crop Body Ritual",
      "Firm Skin Body Wrap",
      "Coconut Sugar Body Polish",
      "Eight Greens Phyto Facial",
    ],
  },
  {
    id: "rf-microneedling",
    badge: "Now Offering",
    name: "RF Microneedling",
    nameFr: "RF Microneedling",
    tagline: "Skin tightening · Acne scars · Stretch marks",
    taglineFr: "Raffermissement · Cicatrices · Vergetures",
    blurb:
      "Fractional radio-frequency energy through ultra-fine needles. Tightens skin, refines texture and stimulates deep collagen — face or body. With a Medical Aesthetician at Ottawa.",
    blurbFr:
      "Énergie RF fractionnée par micro-aiguilles. Resserre la peau, affine la texture et stimule le collagène — visage et corps. Avec une esthéticienne médicale à Ottawa.",
    href: "/laser-and-skin#rf-microneedling",
    Icon: Zap,
    accent: ACCENT,
  },
  {
    id: "hydrafacial",
    badge: "Premium Skin Rejuvenation",
    name: "Crown HydraFacial",
    nameFr: "Crown HydraFacial",
    tagline: "Signature · Deluxe · Platinum",
    taglineFr: "Signature · Deluxe · Platinum",
    blurb:
      "Medical-grade resurfacing in 30–60 minutes. Cleanse, exfoliate, extract and infuse — visible glow from your very first appointment.",
    blurbFr:
      "Resurfaçage médical en 30–60 min. Nettoyer, exfolier, extraire, infuser — éclat visible dès la première séance.",
    href: "/hydrafacial",
    Icon: Droplets,
    accent: "#7dd3c0",
  },
  {
    id: "laser-hair-women",
    badge: "Laser Hair Removal",
    name: "Laser Hair · All Areas",
    nameFr: "Épilation Laser · Toutes zones",
    tagline: "From $15 · Brazilian, Full Legs, Underarms… + The House Package",
    taglineFr: "Dès 15 $ · Brésilien, Jambes, Aisselles… + Promo Maison",
    blurb:
      "Medical-grade laser hair removal with our newest, safest platform. Stack The House Package (Buy 6 → 6 small FREE) or go unlimited with the Head-to-Toe plan.",
    blurbFr:
      "Épilation laser médicale avec notre plus récente plateforme. Combinez la Promo Maison (Achetez 6 → 6 petites GRATUITES) ou passez à l'illimité Tête-aux-pieds.",
    href: "/laser-hair-removal",
    Icon: Sparkles,
    accent: "#f3d69b",
  },
  {
    id: "laser-hair-men",
    badge: "Laser Hair Removal",
    name: "Men's Laser Hair · By Area",
    nameFr: "Épilation Laser Hommes · par zone",
    tagline: "From $15 · Beard, Back, Shoulders, Chest…",
    taglineFr: "Dès 15 $ · Barbe, Dos, Épaules, Poitrine…",
    blurb:
      "Same priced-by-area menu as everyone — unified Feb 2026. Discreet treatment rooms, personalized settings, long-term reduction across every area.",
    blurbFr:
      "Même menu par zone pour tous — unifié en février 2026. Salles discrètes, réglages personnalisés, réduction durable.",
    href: "/laser-hair-removal",
    Icon: Crown,
    accent: "#9ca3af",
  },
  {
    id: "botox",
    badge: "Injectables",
    name: "Botox",
    nameFr: "Botox",
    tagline: "Forehead · 11s · Crow's Feet · Lip Flip",
    taglineFr: "Front · 11 · Pattes d'oie · Lip Flip",
    blurb:
      "Refined, restraint-first dosing. Smooths expression lines while preserving the warmth of your natural face. Available at all 3 clinics.",
    blurbFr:
      "Doses raffinées, retenue avant tout. Lisse les rides d'expression tout en préservant la chaleur du visage. Aux 3 cliniques.",
    href: "/botox-ottawa",
    Icon: Syringe,
    accent: "#f87171",
  },
  {
    id: "dermal-fillers",
    badge: "Injectables",
    name: "Dermal Fillers",
    nameFr: "Agents de Comblement",
    tagline: "Lips · Cheeks · Jawline · Tear Trough",
    taglineFr: "Lèvres · Joues · Mâchoire · Cernes",
    blurb:
      "Hyaluronic-acid sculpting for definition, balance and volume that looks born-with-you, never overdone.",
    blurbFr:
      "Sculpture à l'acide hyaluronique pour définition et volume — naturel, jamais excessif.",
    href: "/dermal-fillers",
    Icon: Sparkles,
    accent: "#c4b5fd",
  },
  {
    id: "sculptra",
    badge: "Bio-Stimulator",
    name: "Sculptra",
    nameFr: "Sculptra",
    tagline: "Collagen restoration · Long-term lift",
    taglineFr: "Restauration de collagène · Soulèvement",
    blurb:
      "Stimulates your own collagen over months for a gradual, natural rejuvenation. Best for clients who want subtle progress, not a reveal.",
    blurbFr:
      "Stimule votre propre collagène sur plusieurs mois pour un rajeunissement progressif et naturel.",
    href: "/sculptra",
    Icon: Heart,
    accent: "#fbbf24",
  },
  {
    id: "skin-boosters",
    badge: "Hydration & Glow",
    name: "Skin Boosters",
    nameFr: "Skin Boosters",
    tagline: "Redensity 1 · Baby Glow · Profhilo",
    taglineFr: "Redensity 1 · Baby Glow · Profhilo",
    blurb:
      "Micro-injections of hyaluronic acid that hydrate from within. The 'lit-from-inside' glow our clients ask for by name.",
    blurbFr:
      "Micro-injections d'acide hyaluronique qui hydratent en profondeur. Le « glow venu de l'intérieur ».",
    href: "/skin-boosters",
    Icon: Droplets,
    accent: "#5eead4",
  },
];

// ───────────────────────────────────────────────────────────────────────────
// NowOfferingReel — mini looping video header for cards that ship reels.
// Auto-advances to the next clip when the current one ends, so the visible
// slot cycles through every ritual (~15s Stone Crop, ~5s each of the rest).
// Muted + playsInline is required for iOS autoplay.
function NowOfferingReel({ videos, labels = [], accent, accentDeep }) {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const advance = () => setIdx((i) => (i + 1) % videos.length);
    v.addEventListener("ended", advance);
    // Guard: some browsers won't fire "ended" if the tab is throttled.
    // Fall back to a safety timer of 16 s so the reel never stalls.
    const safety = setTimeout(advance, 16000);
    return () => {
      v.removeEventListener("ended", advance);
      clearTimeout(safety);
    };
  }, [idx, videos.length]);

  return (
    <div
      className="relative w-full overflow-hidden bg-black"
      style={{ aspectRatio: "16 / 9" }}
      data-testid="now-offering-reel"
    >
      <video
        ref={ref}
        key={videos[idx]}
        src={videos[idx]}
        autoPlay
        muted
        playsInline
        preload="metadata"
        aria-label={labels[idx] || "Éminence body ritual reel"}
        className="w-full h-full object-cover block"
      />
      {/* Bottom-left caption ribbon that names the ritual on screen. */}
      {labels[idx] && (
        <span
          className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded-full"
          style={{
            background: `linear-gradient(135deg, ${accentDeep}dd, ${accent}dd)`,
            color: "#0a1f14",
          }}
        >
          {labels[idx]}
        </span>
      )}
      {/* Tiny dot pager so guests know there are more clips. */}
      <div className="absolute bottom-2 right-2 flex gap-1 pointer-events-none">
        {videos.map((_, i) => (
          <span
            key={i}
            className="w-1.5 h-1.5 rounded-full transition-all"
            style={{ background: i === idx ? accent : "rgba(255,255,255,0.35)" }}
          />
        ))}
      </div>
    </div>
  );
}

export default function NowOfferingRotation() {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [spotlight, setSpotlight] = useState(0);
  // Server-driven config. Defaults match the hard-coded array so the rail
  // looks the same before the seeder writes the first doc.
  const [serverOrder, setServerOrder] = useState(null);
  const [highlight, setHighlight] = useState("rf-microneedling");
  const [hidden, setHidden] = useState([]);
  const [accents, setAccents] = useState({}); // { card_id: "#rrggbb" }

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/now-offering/config`, { timeout: 6000 })
      .then(({ data }) => {
        if (cancelled) return;
        if (Array.isArray(data?.order) && data.order.length) {
          setServerOrder(data.order);
        }
        if (data?.highlight) setHighlight(data.highlight);
        if (Array.isArray(data?.hidden)) setHidden(data.hidden);
        if (data?.accents && typeof data.accents === "object") {
          setAccents(data.accents);
        }
      })
      .catch(() => {
        // Silent: fall through to the default OFFERINGS order.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reorder + filter the hard-coded card list based on the live config.
  const cards = useMemo(() => {
    const byId = Object.fromEntries(OFFERINGS.map((o) => [o.id, o]));
    const order = serverOrder && serverOrder.length
      ? serverOrder
      : OFFERINGS.map((o) => o.id);
    const seen = new Set(order);
    // Append any local cards the server hasn't seen yet (future-proof so a
    // newly added treatment shows up even before the admin edits).
    const finalOrder = [
      ...order,
      ...OFFERINGS.map((o) => o.id).filter((id) => !seen.has(id)),
    ];
    const hideSet = new Set(hidden);
    return finalOrder
      .filter((id) => byId[id] && !hideSet.has(id))
      .map((id) => ({
        ...byId[id],
        // Seasonal accent override from Head Office — falls back to the
        // card's own colour when no override is set.
        accent: accents[id] || byId[id].accent,
        isNew: id === highlight,
      }));
  }, [serverOrder, highlight, hidden, accents]);

  // Rotate the spotlight pill across visible cards every 4.5s so the eye is
  // drawn around the rail without the page itself moving.
  useEffect(() => {
    if (!cards.length) return undefined;
    const t = setInterval(
      () => setSpotlight((i) => (i + 1) % cards.length),
      4500
    );
    return () => clearInterval(t);
  }, [cards.length]);

  // Guard against `spotlight` overshooting when the visible-card count
  // shrinks (e.g. owner hides one). Render-time modulo so we never index
  // past the end without setting state synchronously inside an effect.
  const spotIdx = cards.length ? spotlight % cards.length : 0;

  if (!cards.length) return null;

  return (
    <section
      className="py-14 sm:py-20 bg-gradient-to-b from-stone-50 via-white to-stone-50"
      data-testid="now-offering-rotation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* HEADER */}
        <div className="text-center mb-10 sm:mb-12">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{
              background: `linear-gradient(135deg, ${ACCENT_DEEP}, ${ACCENT})`,
              boxShadow: `0 6px 18px ${ACCENT_DEEP}33, inset 0 1px 0 rgba(255,255,255,0.25)`,
            }}
          >
            <Flame className="w-3.5 h-3.5 text-white" />
            <span
              className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-white font-semibold"
              style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}
            >
              {isFr ? "À l'affiche maintenant" : "Now Offering"}
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl text-stone-900 mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {isFr ? (
              <>
                Les <span className="italic" style={{ color: ACCENT_DEEP }}>nouveautés</span> de la clinique
              </>
            ) : (
              <>
                What&apos;s <span className="italic" style={{ color: ACCENT_DEEP }}>new</span> at the clinic
              </>
            )}
          </h2>
          <p className="text-stone-500 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            {isFr
              ? "Nouveau : soins Éminence Bio · RF Microneedling · HydraFacial · Laser, plus nos injectables classiques. Chaque soin à un toucher de votre prochain rendez-vous."
              : "New: Éminence Organic Facials · RF Microneedling · HydraFacial · Laser Hair Removal — plus our core injectables. Every treatment, one tap away from booking."}
          </p>
        </div>

        {/* RAIL — horizontal scroll on mobile, grid on desktop */}
        <div
          className="flex lg:grid lg:grid-cols-4 gap-4 sm:gap-5 overflow-x-auto lg:overflow-visible snap-x snap-mandatory lg:snap-none -mx-4 px-4 lg:mx-0 lg:px-0 pb-4 lg:pb-0 scrollbar-hide"
          data-testid="now-offering-rail"
        >
          {cards.map((o, i) => {
            const isSpotlight = i === spotIdx;
            return (
              <Link
                key={o.id}
                to={o.href}
                onClick={() => window.scrollTo(0, 0)}
                data-testid={`now-offering-${o.id}`}
                className="group relative shrink-0 w-[78vw] sm:w-[58vw] md:w-[44vw] lg:w-auto snap-start bg-white rounded-2xl overflow-hidden border transition-all hover:-translate-y-1"
                style={{
                  borderColor: isSpotlight ? `${o.accent}cc` : "#e7e5e4",
                  boxShadow: isSpotlight
                    ? `0 18px 40px ${o.accent}26, 0 0 0 1px ${o.accent}55`
                    : "0 1px 2px rgba(0,0,0,0.04)",
                }}
              >
                {/* Top accent bar — colour-codes the treatment family at a
                    glance, so RF / HydraFacial / Laser / Botox each look
                    distinct. */}
                <div
                  aria-hidden
                  className="h-1.5 w-full"
                  style={{
                    background: `linear-gradient(90deg, ${o.accent}, ${o.accent}80)`,
                  }}
                />
                {/* Optional Kling 2 reel — cycles through every video in
                    the card's `videos` array. Muted, looping, playsInline,
                    poster-less for a smooth swap; caption ribbon at the
                    bottom names the ritual on-screen. */}
                {Array.isArray(o.videos) && o.videos.length > 0 && (
                  <NowOfferingReel
                    videos={o.videos}
                    labels={o.videoLabels}
                    accent={o.accent}
                    accentDeep={ACCENT_DEEP}
                  />
                )}
                <div className="p-5 sm:p-6">
                  {/* Badge pill — mirrors the SectionHeader bronze pill on
                      the Laser & Skin page for visual continuity. */}
                  <div className="flex items-center gap-2 mb-4">
                    <div
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                      style={{
                        background:
                          o.isNew
                            ? `linear-gradient(135deg, ${ACCENT_DEEP}, ${ACCENT})`
                            : `${o.accent}1a`,
                        border: o.isNew ? "none" : `1px solid ${o.accent}55`,
                      }}
                    >
                      {o.isNew && (
                        <Flame className="w-3 h-3 text-white" />
                      )}
                      <span
                        className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-semibold"
                        style={{
                          color: o.isNew ? "#fff" : ACCENT_DEEP,
                          fontFamily: "'Inter', -apple-system, sans-serif",
                        }}
                      >
                        {o.badge}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                      style={{
                        background: `${o.accent}1a`,
                        color: ACCENT_DEEP,
                      }}
                    >
                      <o.Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className="text-lg sm:text-xl font-medium text-stone-900 leading-tight"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {isFr ? o.nameFr : o.name}
                      </h3>
                      <p
                        className="text-[11px] sm:text-xs mt-0.5 leading-snug"
                        style={{ color: ACCENT_DEEP }}
                      >
                        {isFr ? o.taglineFr : o.tagline}
                      </p>
                    </div>
                  </div>

                  <p className="text-stone-600 text-sm leading-relaxed mb-4 min-h-[3.5rem]">
                    {isFr ? o.blurbFr : o.blurb}
                  </p>

                  <div
                    className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider transition-all"
                    style={{ color: ACCENT_DEEP }}
                  >
                    {isFr ? "Voir et réserver" : "View & Book"}
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Scroll hint on mobile */}
        <p className="lg:hidden text-center text-stone-400 text-[11px] uppercase tracking-[0.2em] mt-4">
          {isFr ? "← Glissez pour voir tous les soins →" : "← Swipe to see every treatment →"}
        </p>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
}
